import React, {
  useEffect,
  useMemo,
  useRef,
  useLayoutEffect,
  useState,
} from "react";
import * as d3 from "d3";
import "./DataEstateSensitiveCard.scss";
import { useMediaQuery } from "material-ui-core";

const CLASSIFICATION_COLORS = {
  PCI: "#e74c3c",
  PII: "#3498db",
  "PHI/HIPAA": "#16a085",
  GDPR: "#9b59b6",
  Confidential: "#27ae60",
  Financial: "#f1c40f",
};

const DOT_RADIUS = 1.4;
const COLLAPSED_DOT_CAP = 250;

const seededRandom = (seed) => {
  let x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

const normalizeArrayData = (dataArray) => {
  const result = {};

  if (!Array.isArray(dataArray)) return result;

  dataArray.forEach((item) => {
    const combination =
      item?.["FileClassificationStats.classificationCombination"];

    const count =
      parseInt(item?.["FileClassificationStats.fileCount"], 10) || 0;

    if (!combination) return;

    const normalizedKey = combination
      .split(",")
      .map((s) => s.trim())
      .join(",");

    result[normalizedKey] = count;
  });

  return result;
};

export default function RegulatedScatterChart({
  data,
  expanded = false,
  activeFilter = null,
  location = null,
}) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const nodeRef = useRef(null);
  const zoomRef = useRef(null);

  const formattedData = useMemo(() => {
    if (location === "dataEstate") {
      return normalizeArrayData(data);
    }
    return data || {};
  }, [data, location]);

  const [size, setSize] = useState([0, 0]);

  const isSmall = useMediaQuery("(max-width:1600px)");
  const isMedium = useMediaQuery("(max-width:1800px)");

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width && height) setSize([width, height]);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const normalizedData = useMemo(() => {
    if (!formattedData) return [];

    if (Array.isArray(formattedData)) {
      return formattedData
        .map((d) => ({
          combo:
            typeof d.combo === "string"
              ? d.combo
              : typeof d.classification === "string"
                ? d.classification
                : null,
          count: Number(d.count ?? d.value ?? 0),
        }))
        .filter((d) => d.combo);
    }

    return Object.entries(formattedData).map(([combo, count]) => ({
      combo,
      count: Number(count),
    }));
  }, [formattedData]);

  const circleSizes = useMemo(() => {
    const map = new Map();
    normalizedData.forEach(({ combo, count }) => {
      if (!combo || typeof combo !== "string") return;

      combo.split(",").forEach((c) => {
        const key = c.trim();
        if (!key) return;
        map.set(key, (map.get(key) || 0) + count);
      });
    });
    return map;
  }, [normalizedData]);

  const links = useMemo(() => {
    const linkMap = new Map();

    normalizedData.forEach(({ combo, count }) => {
      if (!combo || typeof combo !== "string") return;

      const sets = combo.split(",").map((s) => s.trim());
      if (sets.length < 2) return;

      for (let i = 0; i < sets.length; i++) {
        for (let j = i + 1; j < sets.length; j++) {
          const key = [sets[i], sets[j]].sort().join("--");
          linkMap.set(key, (linkMap.get(key) || 0) + count);
        }
      }
    });

    return Array.from(linkMap.entries()).map(([key, value]) => {
      const [source, target] = key.split("--");
      return { source, target, value };
    });
  }, [normalizedData]);

  const layoutConfig = useMemo(() => {
    if (expanded) return { minRadius: 40, maxRadius: 100 };
    if (isSmall) return { minRadius: 15, maxRadius: 30 };
    if (isMedium) return { minRadius: 20, maxRadius: 35 };
    return { minRadius: 30, maxRadius: 45 };
  }, [expanded, isSmall, isMedium]);

  useEffect(() => {
    if (!size[0] || !circleSizes.size) return;

    d3.select(svgRef.current).selectAll("*").remove();
    d3.select(containerRef.current).selectAll(".regulated-tooltip").remove();

    const width = size[0];
    const height = size[1];
    const cx = width / 2;
    const cy = height / 2;

    const svg = d3
      .select(svgRef.current)
      .append("svg")
      .attr("width", width)
      .attr("height", height);

    const zoomLayer = svg.append("g");
    zoomRef.current = zoomLayer;

    const nodes = Array.from(circleSizes.entries()).map(([id, value]) => ({
      id,
      value,
    }));

    const radiusScale = d3
      .scaleSqrt()
      .domain([0, d3.max(nodes, (d) => d.value)])
      .range([layoutConfig.minRadius, layoutConfig.maxRadius]);

    nodes.forEach((d) => (d.r = radiusScale(d.value)));

    const maxShared = d3.max(links, (l) => l.value) || 1;

    const distanceScale = d3
      .scaleLinear()
      .domain([0, maxShared])
      .range([expanded ? 320 : 140, expanded ? 180 : 70]);

    const simulation = d3
      .forceSimulation(nodes)
      .force("center", d3.forceCenter(cx, cy))
      .force(
        "link",
        d3
          .forceLink(links)
          .id((d) => d.id)
          .distance((d) => distanceScale(d.value))
          .strength(expanded ? 0.45 : 0.8),
      )
      .force("charge", d3.forceManyBody().strength(expanded ? -70 : -25))
      .force(
        "collision",
        d3
          .forceCollide()
          .radius((d) => d.r * (expanded ? 0.28 : 0.18))
          .strength(0.3),
      )
      .stop();

    for (let i = 0; i < 300; i++) simulation.tick();

    nodeRef.current = nodes;

    const tooltip = d3
      .select(containerRef.current)
      .append("div")
      .attr("class", "regulated-tooltip")
      .style("position", "absolute")
      .style("pointer-events", "none")
      .style("opacity", 0)
      .style("background", "#ffffff")
      .style("padding", "8px 12px")
      .style("border-radius", "6px")
      .style("box-shadow", "0 4px 12px rgba(0,0,0,0.12)")
      .style("font-size", "12px")
      .style("color", "#333")
      .style("border", "1px solid #eee");

    const dotsLayer = zoomLayer.append("g");

    nodes.forEach((d, nodeIndex) => {
      const dotCount = expanded
        ? Math.round(d.value)
        : Math.min(Math.round(d.value), COLLAPSED_DOT_CAP);

      for (let i = 0; i < dotCount; i++) {
        let r;
        let angle;

        if (expanded) {
          r =
            d.r *
            (0.6 + seededRandom(nodeIndex * 10000 + i * 13)) *
            Math.sqrt(seededRandom(nodeIndex * 15000 + i * 17));
        } else {
          r = d.r * Math.sqrt(seededRandom(nodeIndex * 10000 + i * 13));
        }

        angle = seededRandom(nodeIndex * 20000 + i * 29) * Math.PI * 2;

        dotsLayer
          .append("circle")
          .attr("class", "dot")
          .datum(d)
          .attr("cx", d.x + r * Math.cos(angle))
          .attr("cy", d.y + r * Math.sin(angle))
          .attr("r", DOT_RADIUS)
          .attr("fill", CLASSIFICATION_COLORS[d.id])
          .attr("opacity", 0.75);
      }
    });

    zoomLayer
      .selectAll("text.label")
      .data(nodes)
      .enter()
      .append("text")
      .attr("class", "label")
      .attr("x", (d) => d.x)
      .attr("y", (d) => d.y)
      .attr("text-anchor", "middle")
      .attr("dy", "0.35em")
      .text((d) => d.id)
      .style("font-weight", "700")
      .style("font-size", expanded ? "16px" : "12px")
      .on("mouseover", (event, d) => {
        tooltip
          .html(`<strong>${d.id}</strong>: <strong>${d.value}</strong>`)
          .style("left", event.offsetX + 20 + "px")
          .style("top", event.offsetY + 10 + "px")
          .transition()
          .style("opacity", 1);
      })
      .on("mouseout", () => tooltip.transition().style("opacity", 0));

    const zoom = d3
      .zoom()
      .scaleExtent([0.7, 3])
      .on("zoom", (event) => {
        zoomLayer.attr("transform", event.transform);
      });

    svg.call(zoom);
  }, [circleSizes, links, size, expanded, layoutConfig]);

  useEffect(() => {
    if (!zoomRef.current) return;

    zoomRef.current
      .selectAll(".dot")
      .transition()
      .duration(250)
      .attr("fill", (d) =>
        !activeFilter || d.id === activeFilter
          ? CLASSIFICATION_COLORS[d.id]
          : "#d3d3d3",
      )
      .attr("opacity", (d) =>
        !activeFilter || d.id === activeFilter ? 0.75 : 0.12,
      );

    zoomRef.current
      .selectAll(".label")
      .transition()
      .duration(250)
      .style("opacity", (d) =>
        activeFilter && d.id !== activeFilter ? 0.7 : 1,
      );
  }, [activeFilter]);

  return (
    <div className="ss-canvas" ref={containerRef}>
      <div ref={svgRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
}
